// Compétences et moyens transférés (M24, lot L6-A, D-274) : registre borné au périmètre, texte de référence,
// dates, moyens, provenance (fictif ou acte officiel) et historique des versions. Saisie réservée aux
// comptes ministériels ; montant des moyens financiers affiché seulement avec le droit financier.
import { COMPETENCES_TRANSFERABLES, NATURES_MOYEN, STATUTS_COMPETENCE, type CompetenceTransferee } from '@cc/contrats';
import { Bouton, Dialogue, EtatVide, PuceEtat, Selection, Squelette, Tableau, type ColonneTableau } from '@cc/ui';
import { Download, History, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useSession } from '../../session/FournisseurSession';
import { exporter, useCompetences, useHistoriqueCompetence, useRepertoire, type Filtres } from './api';
import { FiltresTerritoire, NavigationSupervision, provincesDu } from './Communs';
import { FormulaireCompetence } from './FormulaireCompetence';
import { dateCalcul, valeur } from './logique';
import styles from '../achats/achats.module.css';

const texteMoyens = (c: CompetenceTransferee) =>
  c.moyens.length === 0
    ? 'aucun moyen saisi'
    : c.moyens.map((m) => `${NATURES_MOYEN[m.nature]} : ${m.libelle}${m.quantite !== null ? ` (${m.quantite}${m.unite ? ` ${m.unite}` : ''})` : ''}${m.montant_annuel !== undefined && m.montant_annuel !== null ? `, ${valeur(m.montant_annuel, 'montant', c.fictif)} par an` : ''}`).join(' ; ');

function Historique({ id, onFermer }: { id: string; onFermer: () => void }) {
  const h = useHistoriqueCompetence(id);
  return (
    <Dialogue ouvert titre="Historique des versions" description="Chaque version garde son instantané, son motif et l'institution qui l'a saisie, sans nom de personne." onFermer={onFermer} actions={<Bouton onClick={onFermer}>Fermer</Bouton>}>
      {h.isPending ? (
        <Squelette lignes={3} />
      ) : (
        <ol>
          {(h.data?.elements ?? []).map((v) => (
            <li key={v.version}>
              Version {v.version}, {dateCalcul(v.date)}, {v.institution} : {v.motif} ({String(v.instantane.texte_reference ?? '')}, {STATUTS_COMPETENCE[v.instantane.statut as keyof typeof STATUTS_COMPETENCE] ?? String(v.instantane.statut)})
            </li>
          ))}
        </ol>
      )}
    </Dialogue>
  );
}

export function PageCompetences() {
  const { contexte } = useSession();
  const ministere = contexte?.appartenance_active?.institution === 'ministere';
  const [filtres, setFiltres] = useState<Filtres & { competence?: string }>({});
  const tout = useRepertoire({});
  const { provinces, imposee } = provincesDu(tout.data?.perimetre, tout.data?.elements ?? []);
  const competences = useCompetences(filtres);
  const [historique, setHistorique] = useState<string | null>(null);
  const [edition, setEdition] = useState<CompetenceTransferee | 'nouvelle' | null>(null);
  const lignes = competences.data?.elements ?? [];
  const colonnes: ColonneTableau<CompetenceTransferee>[] = [
    { id: 'competence', entete: 'Compétence', enTeteLigne: true, masquable: false, cellule: (c) => c.libelle_competence },
    { id: 'collectivite', entete: 'Collectivité', cellule: (c) => c.nom },
    { id: 'texte', entete: 'Texte de référence', cellule: (c) => `${c.texte_reference}${c.fictif ? ' (fictif)' : ''}` },
    { id: 'dates', entete: 'Date du texte et d\'effet', cellule: (c) => `${c.date_texte} ; effet ${c.date_effet}` },
    { id: 'statut', entete: 'Statut', cellule: (c) => <PuceEtat ton={c.statut === 'en_vigueur' ? 'ok' : 'neutre'} libelle={STATUTS_COMPETENCE[c.statut]} /> },
    { id: 'moyens', entete: 'Moyens transférés', cellule: texteMoyens },
    { id: 'provenance', entete: 'Provenance', cellule: (c) => c.provenance },
    {
      id: 'actes',
      entete: 'Actions',
      masquable: false,
      cellule: (c) => (
        <span className={styles.actesLigne}>
          <Bouton variante="secondaire" icone={History} onClick={() => setHistorique(c.competence_id)}>Historique (v{c.version})</Bouton>
          {ministere ? <Bouton variante="secondaire" icone={Pencil} onClick={() => setEdition(c)}>Modifier</Bouton> : null}
        </span>
      ),
    },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Supervision"
        titre="Compétences et moyens transférés"
        description="Registre saisi par les comptes ministériels autorisés, borné à leur périmètre. Les lignes des collectivités fictives sont distinguées des actes officiels ; la liste des compétences est indicative, à confirmer par l'administration."
        actions={ministere ? <Bouton variante="principal" icone={Plus} onClick={() => setEdition('nouvelle')}>Nouvelle compétence</Bouton> : null}
      />
      <NavigationSupervision actif="competences" />
      <div className={styles.grille}>
        <FiltresTerritoire filtres={filtres} onChange={(f) => setFiltres((x) => ({ ...x, ...f }))} provinces={provinces} imposee={imposee} />
        <Selection libelle="Compétence" optionVide="Toutes les compétences" options={Object.entries(COMPETENCES_TRANSFERABLES).map(([v, l]) => ({ valeur: v, libelle: l }))} value={filtres.competence ?? ''} onChange={(e) => setFiltres((x) => ({ ...x, competence: e.target.value || undefined }))} />
      </div>
      <div className={styles.actes}>
        <Bouton icone={Download} onClick={() => void exporter('competences', 'csv', { province: filtres.province, type: filtres.type, competence: filtres.competence })}>Exporter en CSV</Bouton>
        <Bouton icone={Download} onClick={() => void exporter('competences', 'xlsx', { province: filtres.province, type: filtres.type, competence: filtres.competence })}>Exporter en XLSX</Bouton>
      </div>
      <EtatRequete chargement={competences.isPending} erreur={competences.error} onReessayer={() => void competences.refetch()} lot="L6-A" fonction="Le registre des compétences">
        <section className={styles.bloc} aria-labelledby="titre-competences">
          <h2 id="titre-competences">Registre ({lignes.length})</h2>
          {lignes.length === 0 ? (
            <EtatVide titre="Aucune compétence enregistrée">Aucune compétence transférée n&apos;est saisie pour ce périmètre et ces filtres.</EtatVide>
          ) : (
            <Tableau legende="Compétences transférées" colonnes={colonnes} lignes={lignes} cleLigne={(c) => c.competence_id} cleColonnes="supervision-competences" />
          )}
        </section>
      </EtatRequete>
      {historique ? <Historique id={historique} onFermer={() => setHistorique(null)} /> : null}
      {edition ? (
        <FormulaireCompetence
          collectivites={(tout.data?.elements ?? []).map((c) => ({ id: c.collectivite_id, nom: c.nom }))}
          existante={edition === 'nouvelle' ? undefined : edition}
          onFermer={() => setEdition(null)}
        />
      ) : null}
    </>
  );
}
